/**
 * What a signed-in person sees, and deliberately not much.
 *
 * Who you are, which Merchant you are acting for, and whether the platform is healthy.
 *
 * Two cards, and which one you get depends on what you are. A Merchant sees Orders; Ops
 * belongs to no Merchant and `POST /orders` refuses them by design, so they see the
 * dispatch console instead. A link that leads to a 403 is worse than no link.
 *
 * **Which links appear is no longer decided here (ADR-0085).** This page used to restate every
 * guard as a condition on its own links — twenty-five of them, including a ten-term disjunction
 * deciding whether the configuration card rendered at all — and the restatement had drifted: the
 * Ops card gated on `session.platformStaff` alone while `RequirePlatformStaff` also requires
 * `role === 'opsUser'`, and every facet was named as a bare string, so one typo would have
 * hidden a screen for ever with nothing failing. `reachableScreens` applies the guards' own
 * rules, and the labels come from the same table the rail reads, so the two surfaces cannot read
 * differently about one screen.
 *
 * **What stays here is editorial: which card advertises which screen.** That is a judgement
 * about this page rather than an authority, so it is a list rather than a derivation — and
 * `landing.test.tsx` holds the two lists to `SCREENS`, so a new screen cannot be forgotten by
 * this surface the way ten of them were forgotten by every surface.
 *
 * Whether this page should advertise screens at all now that the rail lists them is the kind of
 * question a timed walk settles rather than a judgement made here; it is written down as D10's.
 *
 * The "not built yet" list is what remains true, not a permanent fixture.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getHealth, type HealthView, type SessionView } from '../api/client.js';
import { formatNumber, type Locale } from '../i18n/index.js';
import { localName } from '../i18n/names.js';
import { useSession } from '../auth/session.js';
import { InvitationsCard } from './invitations-card.js';
import { holdsAuthority, reachableScreens } from '../nav/screens.js';

type Load =
  | { readonly state: 'loading' }
  | { readonly state: 'ready'; readonly health: HealthView }
  | { readonly state: 'error' };

/**
 * The screens the dispatch card advertises, in the order it shows them.
 *
 * Paths rather than conditions: whether a reader may open one is `SCREENS`' answer, and asking
 * twice is what let the two statements disagree. These are the cross-tenant screens — every read
 * on them is a recorded crossing, which is what the card's own blurb says.
 */
export const OPS_CARD = [
  '/ops/couriers',
  '/ops/audit',
  '/ops/credentials',
  '/ops/exceptions',
  '/ops/cash-desk',
  '/ops/sla-breaches',
  '/ops/dispatch',
  '/ops/compliance',
  '/ops',
] as const;

/**
 * The screens the configuration card advertises.
 *
 * Gated on a facet rather than on platform staff (ADR-0060): that flag is the cross-tenant bypass
 * and these tables have no tenant, so an operator holding `policy` or `finance` without the
 * bypass still finds their screens. Two of them ask for staff as well — a rate guide and an
 * invoice each belong to one Merchant, so reading either is a crossing — and `SCREENS` carries
 * that, not this list.
 */
export const CONFIG_CARD = [
  // `/ops/people` was here as well as on the card above, because an `access` holder who is not
  // platform staff would otherwise have had no link to the console they were granted — this page
  // was the application's only navigation surface. The rail lists it for them now, so the
  // duplicate is a workaround whose reason has expired rather than a rule (ADR-0067, ADR-0085).
  '/ops/notifications',
  // The sends log beside the rules it is the other end of (P4-3d). On this card rather than the
  // dispatch card above, although it needs platform staff: its subject is the notification
  // configuration a `notifications` holder already comes here for, not a shipment in flight.
  '/ops/notifications/sends',
  '/ops/directory',
  '/ops/fleet',
  '/ops/geography',
  '/ops/catalogue',
  '/ops/config/tariffs',
  '/ops/billing',
  '/ops/config/screening-policy',
  '/ops/config/cod-custody-licence',
  '/ops/config/screening-validity',
  '/ops/config/fx-rates',
  '/ops/config/vat',
  '/ops/config/vat-registration',
  '/ops/config/remittance-rail',
  '/ops/config/policy',
] as const;

/**
 * The access console, which belongs to neither card above.
 *
 * It was on both of them — the dispatch card by being staff, the configuration card by holding
 * `access` (ADR-0067) — and that duplication is what a derivation cannot express, because
 * "may open" does not say "by which route". Writing the rule down showed the real problem:
 * administering people is neither a shipment in flight nor a number the platform promises by, so
 * it had been put on two cards whose subjects it does not share, each for a different reader.
 *
 * It sits on the access card instead, which is always drawn and whose subject is exactly this:
 * who you are and what you hold. So an `access` holder who is not platform staff lands on a page
 * that offers them the one console they were granted, which neither arrangement above managed.
 */
export const ACCESS_CARD = ['/ops/people'] as const;

/**
 * The reader's own screens, on the same card as their access (P4-3c).
 *
 * Its own list rather than an entry in `ACCESS_CARD`, because that constant names the access console
 * and a settings screen is not one. The card it renders on is `me.title` — "Your access" — which is
 * the card about the reader, and therefore where somebody looks for what the platform shows *them*.
 *
 * This is also the first entry on any card that every Ops person can open: it needs no facet and no
 * staff flag, because every Ops person has a bell.
 */
export const MINE_CARD = ['/me/notifications'] as const;

/** The one link on each card drawn as the primary act, unchanged from when they were hand-written. */
const PRIMARY: readonly string[] = ['/orders', '/ops', '/ops/config/policy'];

export function LandingPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const { state } = useSession();
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const locale = i18n.language as Locale;

  useEffect(() => {
    let cancelled = false;
    getHealth()
      .then((health) => {
        if (!cancelled) setLoad({ state: 'ready', health });
      })
      .catch(() => {
        if (!cancelled) setLoad({ state: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // `RequireSession` guarantees this, but the compiler does not know it.
  if (state.status !== 'authenticated') return <></>;
  const { session } = state;

  // A new person holds nothing until they answer their invitation: it is all this page offers.
  if (session.awaitingInvitation === true) return <InvitationsCard invitations={session.invitations ?? []} />;

  return (
    <>
      <InvitationsCard invitations={session.invitations ?? []} />
      <section className="card">
        <h2>{t('me.title')}</h2>
        <dl>
          <div className="row">
            <dt>{t('me.role')}</dt>
            <dd>{t(`role.${session.role}`)}</dd>
          </div>
          <div className="row">
            <dt>{t('me.tenant')}</dt>
            {/* Ops belong to no Merchant, which is a fact about the model rather than a gap. */}
            <dd>{session.tenant ? localName({ ar: session.tenant.displayNameAr, en: session.tenant.displayNameEn }, locale) : t('me.noTenant')}</dd>
          </div>
          {session.label && (
            <div className="row">
              <dt>{t('me.label')}</dt>
              <dd>{session.label}</dd>
            </div>
          )}
          {session.platformStaff && (
            <div className="row">
              <dt>{t('me.staff')}</dt>
              <dd>
                <span className="pill pill--ok">{t('me.staffYes')}</span>
              </dd>
            </div>
          )}
        </dl>
        <ScreenLinks session={session} paths={ACCESS_CARD} />
        <ScreenLinks session={session} paths={MINE_CARD} />
      </section>

      <section className="card">
        <h2>{t('status.title')}</h2>

        {load.state === 'loading' && <p className="muted">{t('status.checking')}</p>}
        {load.state === 'error' && <p className="muted">{t('status.unreachable')}</p>}

        {load.state === 'ready' && (
          <dl>
            <div className="row">
              <dt>{t('status.api')}</dt>
              <dd>
                <span className={load.health.ok ? 'pill pill--ok' : 'pill pill--bad'}>
                  {load.health.ok ? t('status.up') : t('status.down')}
                </span>
              </dd>
            </div>
            <div className="row">
              <dt>{t('status.db')}</dt>
              <dd>
                <span className={load.health.db === 'up' ? 'pill pill--ok' : 'pill pill--bad'}>
                  {load.health.db === 'up' ? t('status.up') : t('status.down')}
                </span>
              </dd>
            </div>
            {/*
              * **Not shown to a Merchant** (slice 6). The two rows above answer the only question a
              * Merchant has of this card — *is it me or is it you* — while these two are the
              * platform's own. The outbox depth is the one that matters: it is read
              * `withoutTenantScope`, and ARCHITECTURE §6 withholds it from an **anonymous** caller
              * on the stated ground that *"a queue depth is a quantity and tracks order volume"* —
              * which applies with more force to a commercial party than to our own watchdog, since
              * a tenant watching it over time reads every other tenant's activity. The identity
              * provider's name is internal configuration with no meaning here, and reads as `fake`
              * outside production.
              *
              * **The endpoint still sends both to any signed-in principal**, so a Merchant can read
              * them with their own cookie. Narrowing `health.controller.ts`'s `currentPrincipal()`
              * to an Ops principal is the real fix and is raised rather than taken here, because it
              * changes a public endpoint the watchdog and `deploy.sh` both poll.
              */}
            {session.role === 'opsUser' && (
              <>
                <div className="row">
                  <dt>{t('status.outbox')}</dt>
                  <dd>
                    {load.health.outboxPending === null
                      ? t('status.hidden')
                      : formatNumber(load.health.outboxPending, locale)}
                  </dd>
                </div>
                <div className="row">
                  <dt>{t('status.identity')}</dt>
                  <dd>{load.health.identityProvider}</dd>
                </div>
              </>
            )}
          </dl>
        )}
      </section>

      {session.role === 'merchantUser' && (
        <section className="card">
          <div className="card__head card__head--actions">
            <h2>{t('orders.title')}</h2>
            <Link className="btn" to="/statement">
              {t('statement.open')}
            </Link>
            <Link className="btn btn--primary" to="/orders">
              {t('orders.open')}
            </Link>
          </div>
          <p className="muted">{t('orders.landingBlurb')}</p>
        </section>
      )}

      {/*
        One renderer for both cards. Each shows the screens its list names *and* this reader may
        open, labelled from the same table the rail reads; a card with nothing left to show is not
        drawn, which is what replaced the ten-term disjunction that used to decide the second one.
      */}
      {/*
        Still a condition, and still only this one: every screen on the card below is behind the
        cross-tenant bypass, so a reader without it would be shown a card headed "dispatch" whose
        single surviving link is the people console. `holdsAuthority` rather than
        `session.platformStaff` — the flag alone was the drift, since `RequirePlatformStaff` also
        requires the Ops role.
      */}
      {holdsAuthority(session, { kind: 'staff' }) && (
        <ScreenCard
          session={session}
          heading={t('ops.title')}
          blurb={t('ops.landingBlurb')}
          paths={OPS_CARD}
        />
      )}
      <ScreenCard
        session={session}
        heading={t('ops.config.title')}
        blurb={t('ops.config.landingBlurb')}
        paths={CONFIG_CARD}
      />

      {/*
        Two lists, because these are two different kinds of gap and collapsing them is how
        this card went wrong. It used to say the courier app was "not built yet" — which
        stopped being true at M2 slice C (ADR-0036: seven screens, the offline cycle in
        `packages/courier-sync`, and an `eas.json` since ADR-0054) and went on saying it to
        every viewer, because nothing can catch prose.

        "Not built yet" now holds only what is genuinely absent, and "built, not yet proven"
        holds what exists but has no evidence behind it — so *unbuilt* never comes to mean
        *untested*, which is the distinction `docs/decisions/README.md` exists to keep.
      */}
      <section className="card">
        <h2>{t('next.title')}</h2>
        <ul className="todo muted">
          {/* ADR-0007 §3 makes Rooms' per-viewer aliasing an entry gate for M6, and it is
              unconfirmed — so demo steps 6–8 do not exist (ARCHITECTURE Assumption 4). */}
          <li>{t('next.rooms')}</li>
        </ul>
      </section>

      <section className="card">
        <h2>{t('unproven.title')}</h2>
        <ul className="todo muted">
          {/* I5's last unverified claim: the app is built and has never run on hardware. */}
          <li>{t('unproven.courier')}</li>
        </ul>
      </section>
    </>
  );
}

/**
 * One card advertising the screens a list names and this reader may open.
 *
 * The filter is `reachableScreens`, so the authority is the guards' own — and the sort keeps the
 * card's own order rather than the table's, because the two orders answer different questions:
 * the table is grouped by responsibility for the rail, and a card is a sentence about one
 * subject. Labels come from the table either way, so a screen cannot read differently here.
 */
function ScreenCard({
  session,
  heading,
  blurb,
  paths,
}: {
  readonly session: Extract<SessionView, { authenticated: true }>;
  readonly heading: string;
  readonly blurb: string;
  readonly paths: readonly string[];
}): React.JSX.Element | null {
  const offered = reachableScreens(session).filter((screen) => paths.includes(screen.path));
  if (offered.length === 0) return null;

  return (
    <section className="card">
      <div className="card__head card__head--actions">
        <h2>{heading}</h2>
        <ScreenLinks session={session} paths={paths} />
      </div>
      <p className="muted">{blurb}</p>
    </section>
  );
}

/**
 * The links themselves, so the access card can carry one without a heading and a blurb it has
 * no use for.
 *
 * The sort keeps the list's own order rather than the table's, because the two orders answer
 * different questions: the table is grouped by responsibility for the rail, and a card is a
 * sentence about one subject.
 */
function ScreenLinks({
  session,
  paths,
}: {
  readonly session: Extract<SessionView, { authenticated: true }>;
  readonly paths: readonly string[];
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const offered = reachableScreens(session)
    .filter((screen) => paths.includes(screen.path))
    .sort((a, b) => paths.indexOf(a.path) - paths.indexOf(b.path));

  if (offered.length === 0) return null;

  return (
    <>
      {offered.map((screen) => (
        <Link
          key={screen.path}
          className={PRIMARY.includes(screen.path) ? 'btn btn--primary' : 'btn'}
          to={screen.path}
        >
          {t(screen.labelKey)}
        </Link>
      ))}
    </>
  );
}
